
import { useMemo, useState } from 'react';

interface CodeFieldProps {
  onCodeApply: (code: string) => Promise<void> | void;
  onCodeRemove: () => Promise<void> | void;
  appliedCode?: string;
  discount?: number;
  isValid?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
}

export const CodeField = ({
  onCodeApply,
  onCodeRemove,
  appliedCode,
  discount,
  isValid,
  isLoading = false,
  errorMessage,
}: CodeFieldProps) => {
  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loading = useMemo(() => isLoading || isSubmitting, [isLoading, isSubmitting]);

  const handleApply = async () => {
    const value = code.trim();
    if (!value) return;
    setIsSubmitting(true);
    try {
      await onCodeApply(value);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async () => {
    setCode('');
    await onCodeRemove();
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <h3 className="font-medium text-gray-900 mb-3">Referral / Promo Code</h3>
      
      {appliedCode ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className={`w-4 h-4 flex items-center justify-center ${isValid ? 'text-emerald-600' : 'text-red-600'}`}>
                <i className={isValid ? 'ri-check-line' : 'ri-close-line'}></i>
              </div>
              <span className="text-sm font-medium text-gray-900">{appliedCode}</span>
            </div>
            <button
              onClick={handleRemove}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors cursor-pointer"
            >
              Remove
            </button>
          </div>
          
          {isValid && discount && (
            <p className="text-sm text-emerald-600">
              KES {discount.toLocaleString()} discount applied
            </p>
          )}
          
          {!isValid && (
            <p className="text-sm text-red-600">
              {errorMessage ?? 'Invalid or expired code'}
            </p>
          )}
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Enter promo code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            onKeyPress={(e) => e.key === 'Enter' && handleApply()}
          />
          <button
            onClick={handleApply}
            disabled={!code.trim() || loading}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
          >
            {loading ? (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin"></i>
              </div>
            ) : (
              'Apply'
            )}
          </button>
        </div>
      )}
    </div>
  );
};
